import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { FaFolder, FaFile, FaFileImage, FaFilePdf, FaFileWord, FaFileExcel, FaFileArchive, FaFileCode, FaPlus, FaUpload, FaLink } from 'react-icons/fa';
import { useParams, useLocation } from 'react-router-dom';
import roFolderService from "../services/ro-folder.service";
import CreateFolderModal from "./CreateFolderModal";
import FileUploadModal from "./FileUploadModal";

// Custom styles for S3 file structure layout
const tableStyles = {
    table: {
        borderCollapse: 'collapse',
        width: '100%',
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        overflow: 'hidden'
    },
    header: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6'
    },
    headerCell: {
        padding: '12px 8px',
        textAlign: 'left',
        fontWeight: '600',
        color: '#495057',
        borderBottom: '1px solid #dee2e6',
        borderRight: '1px solid #dee2e6'
    },
    row: {
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease'
    },
    rowHover: {
        backgroundColor: '#f8f9fa'
    },
    cell: {
        padding: '12px 8px',
        verticalAlign: 'middle',
        borderRight: '1px solid #f0f0f0'
    },
    folderIcon: {
        color: '#007bff',
        marginRight: '8px',
        fontSize: '20px'
    },
    fileIcon: {
        marginRight: '8px',
        fontSize: '18px'
    },
    brokenLinkIcon: {
        color: '#6c757d',
        fontSize: '12px',
        marginLeft: '4px',
        opacity: '0.7'
    },
    customerText: {
        color: '#6c757d',
        fontSize: '14px'
    },
    descriptionText: {
        color: '#6c757d'
    },
    folderTypeText: {
        color: '#007bff',
        fontWeight: '500'
    },
    fileTypeText: {
        color: '#495057',
        fontWeight: '500'
    },
    tableContainer: {
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        overflow: 'hidden'
    }
};

const ROFolderDemo = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [data, setData] = useState(null);
    const [currentPath, setCurrentPath] = useState("/30615");
    const [navigationHistory, setNavigationHistory] = useState(["/30615"]);
    
    // Input parameters state
    const [clientId, setClientId] = useState("11990d80-6899-48fd-a0b1-e289fad46475");
    const [clientKey, setClientKey] = useState("12345");
    const [roNumber, setRoNumber] = useState("30615");
    const [hasSearched, setHasSearched] = useState(false);

    // Upload functionality state
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [createFolderLoading, setCreateFolderLoading] = useState(false);

    // Delete functionality state
    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [selectedItems, setSelectedItems] = useState({ folders: [], files: [] });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Get RO from URL parameters (for initial load)
    const { ro } = useParams();
    const location = useLocation();
    


    const fetchData = async (roNumberParam) => {
        console.log("Fetching data for RO:", roNumberParam);
        setLoading(true);
        setError(null);
        
        try {
            const response = await roFolderService.getROFolderData(clientId, clientKey, roNumberParam);
            console.log("RO folder data received:", response.data);
            setData(response.data);
            // Use rootPath if available, otherwise fallback to currentPath or construct from roNumber
            const initialPath = response.data.rootPath || response.data.currentPath || `/${roNumberParam}`;
            setCurrentPath(initialPath);
            setNavigationHistory([initialPath]);
            setHasSearched(true);
        } catch (error) {
            console.error("Error fetching RO folder data:", error);
            
            if (error.response) {
                switch (error.response.status) {
                    case 401:
                        setError("Unauthorized access. Please check your credentials.");
                        break;
                    case 404:
                        setError("RO folder not found. Please check the RO number.");
                        break;
                    case 500:
                        setError("Server error. Please try again later.");
                        break;
                    default:
                        setError(`Error ${error.response.status}: ${error.response.data?.message || error.message}`);
                }
            } else if (error.request) {
                setError("Network error. Please check your connection and try again.");
            } else {
                setError("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initialize RO number from URL on component mount
        const currentRO = (() => {
            // First try path parameter
            if (ro) {
                return ro;
            }
            
            // Then try query parameter
            const urlParams = new URLSearchParams(location.search);
            const queryRO = urlParams.get('ro');
            if (queryRO) {
                return queryRO;
            }
            
            // Fallback: try to extract from pathname
            const pathParts = location.pathname.split('/');
            const roIndex = pathParts.findIndex(part => part === 'ro-folder-demo');
            if (roIndex !== -1 && roIndex + 1 < pathParts.length) {
                return pathParts[roIndex + 1];
            }
            
            return "30615"; // Default fallback
        })();
        
        console.log("Initial RO from URL:", currentRO);
        setRoNumber(currentRO);
    }, [ro, location.pathname, location.search]);

    // Auto-clear success messages after 5 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const getFileIcon = (mimeType) => {
        if (!mimeType) return <FaFile className="text-secondary" style={tableStyles.fileIcon} />;
        if (mimeType.startsWith('image/')) return <FaFileImage className="text-primary" style={tableStyles.fileIcon} />;
        if (mimeType === 'application/pdf') return <FaFilePdf className="text-danger" style={tableStyles.fileIcon} />;
        if (mimeType.includes('word') || mimeType.includes('document')) return <FaFileWord className="text-primary" style={tableStyles.fileIcon} />;
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return <FaFileExcel className="text-success" style={tableStyles.fileIcon} />;
        if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar')) return <FaFileArchive className="text-warning" style={tableStyles.fileIcon} />;
        if (mimeType.includes('text') || mimeType.includes('code')) return <FaFileCode className="text-info" style={tableStyles.fileIcon} />;
        return <FaFile className="text-secondary" style={tableStyles.fileIcon} />;
    };

    const getFileType = (mimeType, fileName) => {
        if (!mimeType) {
            // Try to determine from file extension if no mime type
            const extension = fileName ? fileName.split('.').pop()?.toLowerCase() : '';
            switch (extension) {
                case 'pdf': return 'PDF Document';
                case 'jpg':
                case 'jpeg': return 'JPEG Image';
                case 'png': return 'PNG Image';
                case 'gif': return 'GIF Image';
                case 'bmp': return 'BMP Image';
                case 'webp': return 'WebP Image';
                case 'doc':
                case 'docx': return 'Word Document';
                case 'xls':
                case 'xlsx': return 'Excel Spreadsheet';
                case 'zip':
                case 'rar':
                case 'tar': return 'Archive File';
                case 'txt': return 'Text File';
                default: return 'File';
            }
        }
        
        if (mimeType.startsWith('image/')) {
            if (mimeType.includes('jpeg')) return 'JPEG Image';
            if (mimeType.includes('png')) return 'PNG Image';
            if (mimeType.includes('gif')) return 'GIF Image';
            if (mimeType.includes('bmp')) return 'BMP Image';
            if (mimeType.includes('webp')) return 'WebP Image';
            return 'Image File';
        }
        if (mimeType === 'application/pdf') return 'PDF Document';
        if (mimeType.includes('word') || mimeType.includes('document')) return 'Word Document';
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Excel Spreadsheet';
        if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar')) return 'Archive File';
        if (mimeType.includes('text') || mimeType.includes('code')) return 'Text File';
        return 'File';
    };

    const getFolderType = (folderName) => {
        // Determine folder type based on name or provide default
        if (!folderName) return 'Folder';
        
        const lowerName = folderName.toLowerCase();
        if (lowerName.includes('photo') || lowerName.includes('image')) return 'Photo Folder';
        if (lowerName.includes('doc') || lowerName.includes('document')) return 'Document Folder';
        if (lowerName.includes('work') || lowerName.includes('progress')) return 'Work Folder';
        if (lowerName.includes('estimate') || lowerName.includes('estimating')) return 'Estimate Folder';
        return 'Folder';
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 KB';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        
        // For KB display, always show in KB with one decimal place
        if (i === 0) {
            return (bytes / k).toFixed(1) + ' KB';
        } else if (i === 1) {
            return (bytes / k).toFixed(1) + ' KB';
        } else {
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        try {
            const date = new Date(dateString);
            // Format to match the image: MM/DD/YYYY HH:MM am/pm
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'pm' : 'am';
            const displayHours = hours % 12 || 12;
            
            return `${month}/${day}/${year} ${displayHours}:${minutes} ${ampm}`;
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const handleFileClick = (file) => {
        if (!file) return;
        
        // For images, open in our proxy page to avoid cross-origin issues
        if (file.url && file.mimeType && file.mimeType.startsWith('image/')) {
            const proxyUrl = `/ro-folder-demo-img?url=${encodeURIComponent(file.url)}&name=${encodeURIComponent(file.name)}&mimeType=${encodeURIComponent(file.mimeType)}`;
            window.open(proxyUrl, '_blank', 'noopener,noreferrer');
        } else if (file.url) {
            // For non-image files, open directly in new window/tab
            window.open(file.url, '_blank', 'noopener,noreferrer');
        }
    };

    const handleFolderClick = (folder) => {
        if (folder && folder.path) {
            console.log("Navigating to folder:", folder.path);
            setCurrentPath(folder.path);
            setNavigationHistory(prev => [...prev, folder.path]);
        }
    };

    const handleBackClick = () => {
        if (navigationHistory.length > 1) {
            const newHistory = navigationHistory.slice(0, -1);
            const newPath = newHistory[newHistory.length - 1];
            console.log("Navigating back to:", newPath);
            setCurrentPath(newPath);
            setNavigationHistory(newHistory);
        }
    };

    const getCurrentFolderData = () => {
        if (!data || !currentPath) return { folders: [], files: [] };

        // If we're at the root level
        if (currentPath === (data.rootPath || data.currentPath || "/")) {
            return {
                folders: data.folders || [],
                files: data.files || []
            };
        }

        // Find the current folder in the navigation
        const pathParts = currentPath.split('/').filter(Boolean);
        let currentFolderData = data;

        // Navigate through the folder structure
        for (let i = 0; i < pathParts.length; i++) {
            const part = pathParts[i];
            
            // Skip the RO number (first part)
            if (i === 0 && part === roNumber) {
                continue;
            }

            // Look for the folder in the current level
            if (currentFolderData.folders) {
                const folder = currentFolderData.folders.find(f => f.name === part);
                if (folder) {
                    currentFolderData = folder;
                } else {
                    // If folder not found, return empty data
                    return { folders: [], files: [] };
                }
            } else if (currentFolderData.subFolders) {
                const folder = currentFolderData.subFolders.find(f => f.name === part);
                if (folder) {
                    currentFolderData = folder;
                } else {
                    return { folders: [], files: [] };
                }
            } else {
                return { folders: [], files: [] };
            }
        }

        return {
            folders: currentFolderData.subFolders || [],
            files: currentFolderData.files || []
        };
    };

    const currentData = getCurrentFolderData() || { folders: [], files: [] };

    const handleSearch = () => {
        if (!clientId.trim() || !clientKey.trim() || !roNumber.trim()) {
            setError("Please fill in all fields");
            return;
        }
        fetchData(roNumber);
    };

    const handleReset = () => {
        setData(null);
        setCurrentPath(`/${roNumber}`);
        setNavigationHistory([`/${roNumber}`]);
        setError(null);
        setSuccess(null);
        setHasSearched(false);
        // Reset to default values
        setClientId("11990d80-6899-48fd-a0b1-e289fad46475");
        setClientKey("12345");
        setRoNumber("30615");
    };

    // Upload functionality handlers
    const addFolderToStructure = (folderName) => {
        if (!data) return;

        // Create the new folder object
        const newFolder = {
            name: folderName,
            path: currentPath === (data.rootPath || data.currentPath || "/") ? `${data.rootPath || data.currentPath || "/"}/${folderName}` : `${currentPath}/${folderName}`,
            itemCount: 0,
            subFolders: [],
            files: []
        };

        // If we're at the root level
        if (currentPath === (data.rootPath || data.currentPath || "/")) {
            setData(prevData => ({
                ...prevData,
                folders: [...(prevData.folders || []), newFolder]
            }));
        } else {
            // Find and update the current folder in the structure
            const updateFolderStructure = (folderData, targetPath) => {
                if (folderData.path === targetPath) {
                    return {
                        ...folderData,
                        subFolders: [...(folderData.subFolders || []), newFolder]
                    };
                }

                if (folderData.folders) {
                    return {
                        ...folderData,
                        folders: folderData.folders.map(folder => updateFolderStructure(folder, targetPath))
                    };
                }

                if (folderData.subFolders) {
                    return {
                        ...folderData,
                        subFolders: folderData.subFolders.map(folder => updateFolderStructure(folder, targetPath))
                    };
                }

                return folderData;
            };

            setData(prevData => updateFolderStructure(prevData, currentPath));
        }
    };

    const handleCreateFolder = async (folderName) => {
        setCreateFolderLoading(true);
        try {
            // Add the new folder to the in-memory structure
            addFolderToStructure(folderName);
            
            setShowCreateFolderModal(false);
            setError(null);
        } catch (error) {
            console.error("Error creating folder:", error);
            setError("An unexpected error occurred while creating the folder.");
        } finally {
            setCreateFolderLoading(false);
        }
    };

    // Helper function to validate files before upload
    const validateFiles = (files) => {
        if (!files || files.length === 0) {
            return { isValid: false, error: "Please select at least one file to upload." };
        }

        // Check file types
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
        for (let file of files) {
            if (!allowedTypes.includes(file.type)) {
                return { 
                    isValid: false, 
                    error: `File "${file.name}" is not an image file. Only JPG, PNG, GIF, BMP, and WebP files are allowed.` 
                };
            }
        }

        // Check file sizes (assuming 10MB limit, adjust as needed)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        for (let file of files) {
            if (file.size > maxSize) {
                return { 
                    isValid: false, 
                    error: `File "${file.name}" exceeds the maximum size of 10MB.` 
                };
            }
        }

        return { isValid: true, error: null };
    };

    const handleUploadFiles = async (files) => {
        // Validate files before upload
        const validation = validateFiles(files);
        if (!validation.isValid) {
            setError(validation.error);
            return;
        }

        setUploadLoading(true);
        setError(null);
        
        try {
            const response = await roFolderService.uploadFiles(clientId, roNumber, currentPath, files);
            
            // Handle successful response with different status messages
            if (response.data) {
                const message = response.data;
                
                if (message.includes("Files uploaded successfully")) {
                    if (message.includes("All processing completed")) {
                        // Show success message and refresh data
                        setError(null);
                        setSuccess("Files uploaded successfully! All processing completed.");
                        console.log("Upload successful: All processing completed");
                    } else if (message.includes("Processing continues in background")) {
                        // Show info message about background processing
                        setError(null);
                        setSuccess("Files uploaded successfully! Processing continues in background.");
                        console.log("Upload successful: Processing in background");
                    }
                    
                    // Refresh the data to show the new files
                    await fetchData(roNumber);
                    setShowUploadModal(false);
                } else {
                    // Handle other success responses
                    setError(null);
                    setSuccess("Files uploaded successfully!");
                    await fetchData(roNumber);
                    setShowUploadModal(false);
                }
            } else {
                // No response data, assume success
                setError(null);
                setSuccess("Files uploaded successfully!");
                await fetchData(roNumber);
                setShowUploadModal(false);
            }
            
        } catch (error) {
            console.error("Error uploading files:", error);
            
            if (error.response) {
                const status = error.response.status;
                const errorMessage = error.response.data;
                
                switch (status) {
                    case 401:
                        setError("Unauthorized access. Invalid API key.");
                        break;
                    case 400:
                        // Handle specific validation errors from server
                        if (errorMessage) {
                            if (errorMessage.includes("Invalid RO number")) {
                                setError("Invalid RO number format.");
                            } else if (errorMessage.includes("Invalid path")) {
                                setError("Invalid folder path format.");
                            } else if (errorMessage.includes("At least one file is required")) {
                                setError("Please select at least one file to upload.");
                            } else if (errorMessage.includes("Only image files are allowed")) {
                                setError("Only image files (JPG, PNG, GIF, etc.) are allowed.");
                            } else if (errorMessage.includes("exceed the maximum allowed size")) {
                                setError(errorMessage); // Use server's detailed size error message
                            } else {
                                setError(`Validation error: ${errorMessage}`);
                            }
                        } else {
                            setError("Invalid request. Please check your input.");
                        }
                        break;
                    case 413:
                        setError("File too large. Please check file sizes.");
                        break;
                    case 500:
                        // Handle server processing errors
                        if (errorMessage && errorMessage.includes("Unknown processing status")) {
                            setError("Server processing error. Please try again later.");
                        } else if (errorMessage && errorMessage.includes("An error occurred during file processing")) {
                            setError("File processing failed. Please try again later.");
                        } else if (errorMessage && errorMessage.includes("An error occurred while uploading files")) {
                            setError(`Upload failed: ${errorMessage.split(": ")[1] || "Unknown error"}`);
                        } else {
                            setError("Server error. Please try again later.");
                        }
                        break;
                    default:
                        setError(`Server error (${status}): ${errorMessage || error.message}`);
                }
            } else if (error.request) {
                setError("Network error. Please check your connection and try again.");
            } else {
                setError("An unexpected error occurred. Please try again.");
            }
        } finally {
            setUploadLoading(false);
        }
    };

    // Delete functionality handlers
    const toggleDeleteMode = () => {
        if (isDeleteMode) {
            // Exit delete mode
            setIsDeleteMode(false);
            setSelectedItems({ folders: [], files: [] });
        } else {
            // Enter delete mode
            if ((!currentData.folders || currentData.folders.length === 0) && 
                (!currentData.files || currentData.files.length === 0)) {
                setError("Cannot enter delete mode: this folder is empty.");
                return;
            }
            setIsDeleteMode(true);
            setSelectedItems({ folders: [], files: [] });
        }
    };

    const handleItemSelection = (type, item, isSelected) => {
        setSelectedItems(prev => {
            const newItems = { ...prev };
            if (isSelected) {
                newItems[type] = [...newItems[type], item];
            } else {
                newItems[type] = newItems[type].filter(i => i.path !== item.path);
            }
            return newItems;
        });
    };

    const handleDeleteItems = async () => {
        const totalSelected = selectedItems.folders.length + selectedItems.files.length;
        if (totalSelected === 0) {
            setError("Please select at least one item to delete.");
            return;
        }

        setDeleteLoading(true);
        setError(null);

        try {
            // Prepare the data for the API call according to the required format
            const folders = selectedItems.folders.map(folder => folder.path);
            const files = selectedItems.files.map(file => file.path);

            const response = await roFolderService.deleteItems(clientId, roNumber, currentPath, folders, files);
            
            if (response.data) {
                setSuccess("Items deleted successfully!");
                // Refresh the data to show the updated folder structure
                await fetchData(roNumber);
                // Exit delete mode
                setIsDeleteMode(false);
                setSelectedItems({ folders: [], files: [] });
            }
        } catch (error) {
            console.error("Error deleting items:", error);
            
            if (error.response) {
                const status = error.response.status;
                const errorMessage = error.response.data;
                
                switch (status) {
                    case 401:
                        setError("Unauthorized access. Invalid API key.");
                        break;
                    case 400:
                        setError(`Validation error: ${errorMessage || "Invalid request"}`);
                        break;
                    case 500:
                        setError("Server error. Please try again later.");
                        break;
                    default:
                        setError(`Server error (${status}): ${errorMessage || error.message}`);
                }
            } else if (error.request) {
                setError("Network error. Please check your connection and try again.");
            } else {
                setError("An unexpected error occurred. Please try again.");
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <Row className="justify-content-center">
                    <Col md={8} className="text-center">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-2">Loading RO folder data...</p>
                    </Col>
                </Row>
            </Container>
        );
    }



        return (
        <Container className="mt-4">
            {/* Input Form */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h4 className="mb-0">RO Folder Demo - Configuration</h4>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <label htmlFor="clientId" className="form-label">Client ID</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="clientId"
                                            value={clientId}
                                            onChange={(e) => setClientId(e.target.value)}
                                            placeholder="Enter client ID"
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <label htmlFor="clientKey" className="form-label">Client Key</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="clientKey"
                                            value={clientKey}
                                            onChange={(e) => setClientKey(e.target.value)}
                                            placeholder="Enter client key"
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <label htmlFor="roNumber" className="form-label">RO Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="roNumber"
                                            value={roNumber}
                                            onChange={(e) => setRoNumber(e.target.value)}
                                            placeholder="Enter RO number"
                                        />
                                    </div>
                                </Col>
                            </Row>
                            {/* Error Display */}
                            {error && (
                                <Row className="mt-3">
                                    <Col>
                                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                            <Alert.Heading>Error</Alert.Heading>
                                            <p className="mb-2">{error}</p>
                                        </Alert>
                                    </Col>
                                </Row>
                            )}
                            
                            {/* Success Display */}
                            {success && (
                                <Row className="mt-3">
                                    <Col>
                                        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                                            <Alert.Heading>Success</Alert.Heading>
                                            <p className="mb-2">{success}</p>
                                        </Alert>
                                    </Col>
                                </Row>
                            )}
                            
                            <Row>
                                <Col className="text-center">
                                    <button 
                                        className="btn btn-primary me-2" 
                                        onClick={handleSearch}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Searching...
                                            </>
                                        ) : (
                                            'Search RO Folder'
                                        )}
                                    </button>
                                    {hasSearched && (
                                        <button 
                                            className="btn btn-outline-secondary" 
                                            onClick={handleReset}
                                        >
                                            Reset
                                        </button>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Results Section */}
            {hasSearched && data && (
                <Row>
                    <Col>
                        <Card>
                            <Card.Header>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">
                                        RO: {currentPath ? currentPath.replace(/^\//, '') : roNumber}
                                    </h4>
                                    {navigationHistory.length > 1 && (
                                        <button 
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={handleBackClick}
                                        >
                                            ← Back
                                        </button>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {/* Action Buttons */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0">Items</h5>
                                    <div>
                                        {isDeleteMode ? (
                                            <>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={handleDeleteItems}
                                                    disabled={deleteLoading || (selectedItems.folders.length + selectedItems.files.length === 0)}
                                                >
                                                    {deleteLoading ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Deleting...
                                                        </>
                                                    ) : (
                                                        `Confirm Delete (${selectedItems.folders.length + selectedItems.files.length} selected)`
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedItems({ folders: [], files: [] });
                                                        setIsDeleteMode(false);
                                                    }}
                                                    disabled={deleteLoading}
                                                >
                                                    Clear Selection
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => setShowCreateFolderModal(true)}
                                                    disabled={loading}
                                                >
                                                    <FaPlus className="me-1" />
                                                    New Folder
                                                </Button>
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => setShowUploadModal(true)}
                                                    disabled={loading}
                                                >
                                                    <FaUpload className="me-1" />
                                                    Upload Images
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={toggleDeleteMode}
                                                    disabled={loading || (!currentData.folders?.length && !currentData.files?.length)}
                                                >
                                                    Delete
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Delete mode status */}
                                {isDeleteMode && (
                                    <div className="alert alert-warning mb-3">
                                        <strong>Delete Mode Active</strong>
                                        <br />
                                        <small>
                                            Current path: {currentPath}
                                            <br />
                                            Select items to delete. Click on folders or files to select/deselect them.
                                            Use "Clear Selection" to unselect all items and exit delete mode.
                                            {selectedItems.folders.length + selectedItems.files.length > 0 && (
                                                <span className="text-danger ms-2">
                                                    ({selectedItems.folders.length} folder{selectedItems.folders.length !== 1 ? 's' : ''}, 
                                                    {selectedItems.files.length} file{selectedItems.files.length !== 1 ? 's' : ''} selected)
                                                </span>
                                            )}
                                        </small>
                                    </div>
                                )}

                                {/* Table Layout for Folders and Files */}
                                <div className="table-responsive" style={tableStyles.tableContainer}>
                                    <table className="table table-hover" style={tableStyles.table}>
                                        <thead className="table-light" style={tableStyles.header}>
                                            <tr>
                                                <th style={{ width: '50px' }}>
                                                    {isDeleteMode && (
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                (currentData.folders?.length > 0 && selectedItems.folders.length === currentData.folders.length) &&
                                                                (currentData.files?.length > 0 && selectedItems.files.length === currentData.files.length)
                                                            }
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    // Select all
                                                                    const allFolders = currentData.folders || [];
                                                                    const allFiles = currentData.files || [];
                                                                    setSelectedItems({
                                                                        folders: allFolders,
                                                                        files: allFiles
                                                                    });
                                                                } else {
                                                                    // Deselect all
                                                                    setSelectedItems({ folders: [], files: [] });
                                                                }
                                                            }}
                                                            className="form-check-input"
                                                        />
                                                    )}
                                                </th>
                                                <th style={tableStyles.headerCell}>Name</th>
                                                <th style={tableStyles.headerCell}>Type</th>
                                                <th style={tableStyles.headerCell}>Modified</th>
                                                <th style={tableStyles.headerCell}>Size</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Folders */}
                                            {currentData.folders && currentData.folders.length > 0 && 
                                                currentData.folders
                                                    .filter(folder => folder && folder.name)
                                                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                    .map((folder, index) => (
                                                        <tr 
                                                            key={`folder-${index}`}
                                                            className={isDeleteMode && selectedItems.folders.some(f => f.path === folder.path) ? 'table-danger' : ''}
                                                            style={{ 
                                                                ...tableStyles.row,
                                                                cursor: isDeleteMode ? 'default' : 'pointer',
                                                                backgroundColor: isDeleteMode && selectedItems.folders.some(f => f.path === folder.path) ? '#f8d7da' : undefined
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (!isDeleteMode && !e.currentTarget.classList.contains('table-danger')) {
                                                                    e.currentTarget.style.backgroundColor = tableStyles.rowHover.backgroundColor;
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (!isDeleteMode && !e.currentTarget.classList.contains('table-danger')) {
                                                                    e.currentTarget.style.backgroundColor = '';
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                if (isDeleteMode) {
                                                                    const isSelected = selectedItems.folders.some(f => f.path === folder.path);
                                                                    handleItemSelection('folders', folder, !isSelected);
                                                                } else {
                                                                    handleFolderClick(folder);
                                                                }
                                                            }}
                                                        >
                                                            <td style={tableStyles.cell}>
                                                                {isDeleteMode && (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedItems.folders.some(f => f.path === folder.path)}
                                                                        onChange={(e) => handleItemSelection('folders', folder, e.target.checked)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="form-check-input"
                                                                    />
                                                                )}
                                                            </td>
                                                            <td style={tableStyles.cell}>
                                                                <div className="d-flex align-items-center">
                                                                    <FaFolder className="text-primary me-2" size={20} style={tableStyles.folderIcon} />
                                                                    <strong>{folder.name || 'Unnamed Folder'}</strong>
                                                                </div>
                                                            </td>
                                                            <td style={tableStyles.cell}>
                                                                {/* Leave blank for folders */}
                                                            </td>
                                                            <td style={tableStyles.cell}>
                                                                {/* Leave blank for folders */}
                                                            </td>
                                                            <td style={tableStyles.cell}></td>
                                                        </tr>
                                                    ))
                                            }
                                            
                                            {/* Files */}
                                            {currentData.files && currentData.files.length > 0 && 
                                                currentData.files
                                                    .filter(file => file && file.name)
                                                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                    .map((file, index) => (
                                                        <tr 
                                                            key={`file-${index}`}
                                                            className={isDeleteMode && selectedItems.files.some(f => f.path === file.path) ? 'table-danger' : ''}
                                                            style={{ 
                                                                ...tableStyles.row,
                                                                cursor: isDeleteMode ? 'default' : 'pointer',
                                                                backgroundColor: isDeleteMode && selectedItems.files.some(f => f.path === file.path) ? '#f8d7da' : undefined
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (!isDeleteMode && !e.currentTarget.classList.contains('table-danger')) {
                                                                    e.currentTarget.style.backgroundColor = tableStyles.rowHover.backgroundColor;
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (!isDeleteMode && !e.currentTarget.classList.contains('table-danger')) {
                                                                    e.currentTarget.style.backgroundColor = '';
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                if (isDeleteMode) {
                                                                    const isSelected = selectedItems.files.some(f => f.path === file.path);
                                                                    handleItemSelection('files', file, !isSelected);
                                                                } else {
                                                                    handleFileClick(file);
                                                                }
                                                            }}
                                                        >
                                                            <td style={tableStyles.cell}>
                                                                {isDeleteMode && (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedItems.files.some(f => f.path === file.path)}
                                                                        onChange={(e) => handleItemSelection('files', file, e.target.checked)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="form-check-input"
                                                                    />
                                                                )}
                                                            </td>
                                                            <td style={tableStyles.cell}>
                                                                <div className="d-flex align-items-center">
                                                                    {getFileIcon(file.mimeType)}
                                                                    <span className="ms-2">{file.name || 'Unnamed File'}</span>
                                                                </div>
                                                            </td>
                                                            <td style={{...tableStyles.cell, ...tableStyles.fileTypeText}}>{getFileType(file.mimeType, file.name)}</td>
                                                            <td style={tableStyles.cell}>
                                                                <span>{formatDate(file.lastModified)}</span>
                                                            </td>
                                                            <td style={tableStyles.cell}>{formatFileSize(file.size)}</td>
                                                        </tr>
                                                    ))
                                            }
                                        </tbody>
                                    </table>
                                </div>

                                {/* Empty state */}
                                {(!currentData.folders || currentData.folders.length === 0) &&
                                 (!currentData.files || currentData.files.length === 0) && (
                                    <div className="text-center py-4">
                                        <FaFolder className="text-muted" size={48} />
                                        <p className="mt-2 text-muted">This folder is empty</p>
                                        <small className="text-muted">No items available for deletion</small>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Initial state when no search has been performed */}
            {!hasSearched && !data && (
                <Row>
                    <Col>
                        <Card>
                            <Card.Body className="text-center py-5">
                                <FaFolder className="text-muted mb-3" size={64} />
                                <h5 className="text-muted">Enter your credentials and RO number above to search</h5>
                                <p className="text-muted">The form is pre-filled with default values for testing</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Modals */}
            <CreateFolderModal
                show={showCreateFolderModal}
                onHide={() => setShowCreateFolderModal(false)}
                onSubmit={handleCreateFolder}
                currentPath={currentPath}
                loading={createFolderLoading}
            />

            <FileUploadModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onSubmit={handleUploadFiles}
                currentPath={currentPath}
                loading={uploadLoading}
            />
        </Container>
    );
};

export default ROFolderDemo;
